<script>
import AdminPanel from '@components/admin-panel/admin-panel.vue';
import GroceryList from '@components/grocery/grocery-list.vue';
import Layout from '@layouts/main.vue';
import MacrosBar from '@components/macros/macros-bar.vue';
import MealPlanForm from '@components/forms/meal-plan-form.vue';
import PlanGrid from '@components/plan/plan-grid.vue';
import PlanHeader from '@components/plan/plan-header.vue';
import RecipeLibrary from '@components/recipe/recipe-library.vue';
import RecipePreview from '@components/recipe/recipe-preview.vue';
import UseLargeScreen from '@components/use-large-screen.vue';
import { adminPanelMixin } from '@mixins/admin-panel-mixin';
import { breakpointsMixin } from '@mixins/breakpoints-mixin.js';
import {
  adminPanelComputed,
  adminPanelMethods,
  platelistComputed,
  platelistMethods,
  recipeMethods,
  recipeComputed,
  authComputed,
  mediaMethods,
  nutritionMethods,
} from '@state/helpers';

export default {
  page: {
    title: 'New Meal Plan',
    meta: [{ name: 'description', content: 'The New Meal Plan page.' }],
  },
  mixins: [adminPanelMixin, breakpointsMixin],
  props: {
    platelist: {
      type: Object,
      required: true,
      default: () => {},
    },
  },
  data() {
    return {
      isLoading: false,
    };
  },
  computed: {
    ...authComputed,
    ...adminPanelComputed,
    ...platelistComputed,
    ...recipeComputed,
    isPlatelistLocked() {
      return (
        this.selectedPlatelist &&
        this.selectedPlatelist.editabilityStatus &&
        this.selectedPlatelist.editabilityStatus.locked
      );
    },
  },
  components: {
    AdminPanel,
    GroceryList,
    Layout,
    MacrosBar,
    MealPlanForm,
    PlanGrid,
    PlanHeader,
    RecipeLibrary,
    RecipePreview,
    UseLargeScreen,
  },
  async created() {
    this.isLoading = true;
    if (!this.allRecipes || !this.allRecipes.length) {
      await this.getAllRecipes();
    }

    if (!this.adminPlanWeek || !this.allPlatelists.length) {
      this.setSelectedWeekOffset(0);
      await this.getAdminPlanGrid({ platelistId: this.platelist.id });
    }

    if (!this.selectedPlatelist) {
      await this.setSelectedPlatelist(this.platelist);
    }
    this.isLoading = false;
  },
  async mounted() {
    if (!Object.keys(this.recipeFilterOpts).length) {
      this.getAutocompleteLists();
      await this.getRecipeFilterOpts();
    }
  },
  destroyed() {
    this.setSelectedPlatelist(null);
    this.setSelectedRecipe(null);
    this.setSelectedPlanDay(null);
    this.setSelectedMealtime(null);
    this.setAdminPlanWeek(null);
    this.setIsNewMediaAsset(null);
    this.setTempPhotoPreview(null);
  },
  methods: {
    ...adminPanelMethods,
    ...mediaMethods,
    ...nutritionMethods,
    ...platelistMethods,
    ...recipeMethods,
    async showAdminPanel(component, data = null, size = '') {
      await this.setPanelContent(this.getPanelContent(component, data, size));
      this.setShowAdminPanel();
    },
    onHidePanel() {
      if (this.hasUnsavedPlatelistChanges && !this.panelContentDetail) {
        this.$router.app.$bvModal.show('unsaved-changes-modal');
      } else {
        this.setSelectedRecipe(null);
        this.setSelectedPlanDay(null);
        this.setSelectedMealtime(null);
      }
    },
    onShowPlanForm() {
      this.showAdminPanel('MealPlanForm', { panelTitle: 'Edit Plan' });
    },
    onShowGroceryList() {
      this.showAdminPanel('GroceryList');
      this.showAdminPanel('MealPlanForm');
    },
    async onViewRecipe(recipe, isRecipeInPlan) {
      await this.getRecipe({ recipeId: recipe.recipeId || recipe.id });
      await this.setSelectedRecipe({ ...recipe, ...this.recipeDetails });
      this.showAdminPanel(
        'RecipePreview',
        isRecipeInPlan ? { isRecipeInPlan } : null
      );
    },
    onManageRecipe(recipe) {
      this.onViewRecipe(recipe, true);
    },
    async onSelectRecipe(recipe) {
      await this.setIsAddingDish(true);
      this.setSelectedMealtimeDish(recipe);
    },
  },
};
</script>

<template>
  <UseLargeScreen v-if="isSmallScreenView" />
  <Layout v-else>
    <AdminPanel
      v-if="!isLoading"
      :title="panelContent ? panelContent.title : ''"
      :size="panelContent ? panelContent.size : ''"
      :noPad="panelContent ? panelContent.noPad : false"
      @hide-panel="onHidePanel"
    >
      <component
        v-if="panelContent"
        :is="panelContent.component"
        :content="panelContent"
      />
    </AdminPanel>

    <div class="container-wrapper">
      <RecipeLibrary
        v-if="!isLoading"
        @view-recipe-details="onViewRecipe"
        @select-recipe="onSelectRecipe"
      />
      <div class="content-wrapper no-pad">
        <div class="loading-wrapper" v-if="isLoading">
          <BaseLoadingSpinner />
        </div>

        <PlanHeader
          v-if="adminPlanWeek && !isLoading"
          :planWeek="adminPlanWeek"
          @show-plan-form="onShowPlanForm"
          @show-grocery-panel="onShowGroceryList"
        />

        <PlanGrid
          v-if="adminPlanWeek && !isLoading"
          :planWeek="adminPlanWeek"
          @manage-recipe="onManageRecipe"
        />
        <MacrosBar
          v-if="adminPlanWeek && !isLoading"
          :planWeek="adminPlanWeek"
        />
      </div>
    </div>
  </Layout>
</template>

<style lang="scss" scoped>
@import '@design';
// stylelint-disable selector-pseudo-element-no-unknown

.content-wrapper {
  height: 100vh;

  .loading-wrapper {
    align-items: center;
    display: flex;
    justify-content: center;
    margin-bottom: 30vh;
    margin-top: 35vh;
    width: 100%;

    ::v-deep .loading {
      height: 4rem;
      width: 4rem;
    }
  }
}

::v-deep .panel-wrapper.active .panel-container {
  width: 80%;

  @include media('xxl', null) {
    width: 60%;
  }

  @include media('xxxl', null) {
    width: 45%;
  }
}
</style>
